import type { ApolloError } from "@apollo/client";
import type { RowPinningState } from "@tanstack/react-table";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  type InvoiceSearchResult,
  SmbAttachmentStatus,
  type Transaction,
} from "@/apis/gql/types";
import { Badge } from "@/components/ui/badge";
import { ErrorMessage } from "@/components/ui/error";
import { cn } from "@/lib/utils";
import { useGetAllTransactions } from "@/queries/transactions/get-all";

import { useTableInstanceContext } from "@/components/shared/data-table";
import { Button } from "@/components/ui/button";
import { Search } from "lucide-react";
import { getInvoiceMatchingColumns as _columns } from "./invoice-matching-columns";
import { InvoiceMatchingDataTable } from "./invoice-matching-data-table";
import { InvoiceMatchingTableInstanceProvider } from "./invoice-matching-data-table-instance-provider";

type Props = {
  transactionIds: string[];
  onRowSelectionChange: (value: Record<string, boolean>) => void;
  rowSelection: Record<string, boolean>;
  invoices: InvoiceSearchResult[];
  loading: boolean;
  error?: ApolloError;
  onMatch: (invoiceIds: string[]) => Promise<void>;
  onUnmatch: (invoiceIds: string[]) => Promise<void>;
  rowPinningOnMatch: boolean;
  onInvoicesSearchChange: (global: string) => void;
  invoicesSearch: string;
  view?: "documented" | "search";
  className?: string;
  children?:
    | React.ReactNode
    | ((props: { transactions: Transaction[] }) => React.ReactNode);
};

const _InvoiceMatchingForm = ({
  transactionIds,
  children,
  onRowSelectionChange,
  onMatch,
  onUnmatch,
  rowSelection,
  invoices: _invoices,
  loading,
  rowPinningOnMatch,
  error,
  onInvoicesSearchChange,
  invoicesSearch,
  className,
  view: _view,
}: Props) => {
  const { t } = useTranslation();
  const [view, setView] = useState<"documented" | "search">(_view ?? "search");

  const queryParams = useMemo(
    () => ({
      query: {
        bankTransactionId: {
          value: transactionIds,
          op: "in",
        } as any,
      },
      page: 0,
      limit: 50,
      offset: 0,
    }),
    [transactionIds],
  );

  const { data: transactions, loading: transactionsLoading } =
    useGetAllTransactions(queryParams);

  const initializedForTransactionRef = useRef<string | null>(null);

  useEffect(() => {
    if (_view !== undefined) {
      return;
    }

    const transactionKey = transactionIds.join(",");

    if (initializedForTransactionRef.current === transactionKey) {
      return;
    }

    if (transactionsLoading) {
      return;
    }

    initializedForTransactionRef.current = transactionKey;

    if (transactions.length !== 1) {
      setView("search");
      return;
    }

    const transaction = transactions[0];
    const shouldShowDocumented =
      (transaction.smbAttachmentStatus === SmbAttachmentStatus.ATTACHED ||
        transaction.smbAttachmentStatus ===
          SmbAttachmentStatus.ATTACHED_ZERO_TOUCH) &&
      transaction.invoices.length > 0;

    setView(shouldShowDocumented ? "documented" : "search");
  }, [transactionIds, transactions, transactionsLoading]);

  useEffect(() => {
    onInvoicesSearchChange("");
  }, [transactionIds, onInvoicesSearchChange]);

  const handleMatch = useCallback(
    async (invoiceIds: string[]) => {
      await onMatch(invoiceIds);
    },
    [onMatch],
  );

  const handleUnmatch = useCallback(
    async (invoiceIds: string[]) => {
      await onUnmatch(invoiceIds);
    },
    [onUnmatch],
  );

  const columns = useMemo(() => {
    return _columns({
      transactions,
      onMatch: handleMatch,
      onUnmatch: handleUnmatch,
      rowPinningOnMatch,
    });
  }, [transactions, handleMatch, handleUnmatch, rowPinningOnMatch]);

  const matchedInvoiceIds = useMemo(() => {
    return transactions.flatMap((transaction) =>
      [...transaction.invoices]
        .sort((a, b) => {
          if (!a.matchedAt || !b.matchedAt) {
            return -1;
          }
          return (
            new Date(a.matchedAt).getTime() - new Date(b.matchedAt).getTime()
          );
        })
        .map((invoice) => invoice.invoiceId),
    );
  }, [transactions]);

  const rowPinning = useMemo<RowPinningState>(
    () => ({
      top: matchedInvoiceIds,
      bottom: [],
    }),
    [matchedInvoiceIds],
  );

  const invoices = useMemo(() => {
    return _invoices.filter((invoice) => {
      if (view === "documented") {
        const invoiceId = invoice.invoiceId;
        return matchedInvoiceIds.includes(invoiceId);
      }
      return true;
    });
  }, [_invoices, matchedInvoiceIds, view]);

  const handleSwitchToSearch = useCallback(() => {
    setView("search");
  }, []);

  if (error) {
    return (
      <div className="flex-1 flex justify-center items-center h-full">
        <ErrorMessage />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <InvoiceMatchingTableInstanceProvider
        data={invoices}
        columns={columns}
        onRowSelectionChange={onRowSelectionChange}
        rowSelection={rowSelection}
        rowPinning={rowPinning}
        onRowPinningChange={() => {}}
        footer={
          view === "documented" ? (
            <div className="flex justify-center">
              <Button
                variant="outline"
                className="gap-2 mr-2 border-transparent hover:border-transparent self-center"
                onClick={handleSwitchToSearch}
              >
                <Search className="w-4 h-4" />
                {t("add_more_documents")}
              </Button>
            </div>
          ) : (
            true
          )
        }
      >
        {view === "documented" && (
          <Badge
            variant="outline"
            className="w-fit bg-green-50 border-green-600 text-green-700 font-medium mb-2 mx-4"
          >
            {invoices.length === 1
              ? t("documents_matched_one")
              : t("documents_matched", { count: invoices.length })}
          </Badge>
        )}
        <div className={cn("h-full", className)}>
          <InvoiceMatchingDataTable
            loading={loading || transactionsLoading}
            transactions={transactions}
            onInvoicesSearchChange={onInvoicesSearchChange}
            invoicesSearch={invoicesSearch}
            hideHeader={view === "documented"}
          />
        </div>
        {typeof children === "function" ? children({ transactions }) : children}
        <ToggleSelectColumnVisibility view={view} />
      </InvoiceMatchingTableInstanceProvider>
    </div>
  );
};

export const InvoiceMatchingForm = memo(_InvoiceMatchingForm);

const ToggleSelectColumnVisibility = ({
  view,
}: { view: "documented" | "search" }) => {
  const { table } = useTableInstanceContext<InvoiceSearchResult>();

  useEffect(() => {
    table.setColumnVisibility({
      ...table.getState().columnVisibility,
      select: view === "search",
    });
  }, [view]);

  return null;
};
